// to be removed
import { appWithTranslation } from "next-i18next";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import ErrorBoundary from "../components/Layout/ErrorBoundary/ErrorBoundary";
import { MainLayout } from "../components/Layout/MainLayout";
import authHOC from "../components/Layout/auth/authHighOrderComponent";
import { AuthProvider } from "../components/Layout/auth/useAuth";
import { Modal } from "../components/global/Modal/ModalComponent";
import { LoadingDotBars } from "../components/global/NewLoading/LoadingDotBars";
import { NewLoading } from "../components/global/NewLoading/NewLoadingComponent";
import BackgroundHooks from "../services/backgroundRequest/BackgroundHooks";
import BackgroundManager from "../services/backgroundRequest/BackgroundManager";

const OpenMobileApp = dynamic(
  () =>
    import("../components/appRedirection/appRedirection").then(
      (mod) => mod.OpenMobileApp,
    ),
  { ssr: false },
);

const BackgroundRequests = dynamic(
  () => import("../services/backgroundRequest/BackgroundRequests"),
  { ssr: false },
);
import { useAppSelector } from "../services/hooks/useReduxHooks";
import { persistor, store } from "../services/redux/app/store";
import "../styles/_custom.scss";
import "../styles/globals.scss";
import "../utils/APICalls/AxiosConfig"; // Import Axios configuration here
import { getCookie } from "../utils/CookieHelper";
import LS from "../utils/LocalStorage";
import {
  COOKIE_ENUM,
  LocalStorageEnum,
  PagesEnum,
  SOCKET_NAMESPACE,
} from "../utils/enums";
import env from "../utils/env.config";
import { getPageLink } from "../utils/help/navigation/navigation";
import { Page } from "../utils/page";
import { getSocket } from "../utils/socket";
import CookieCheckPage from "./cookie-check";

interface MyAppProps extends AppProps {
  Component: Page;
  pageProps: any;
}

// Component to handle offline mode redirect
function OfflineModeRedirect() {
  const router = useRouter();
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig,
  );

  useEffect(() => {
    // Wait for router to be ready before checking
    if (!router.isReady) {
      return;
    }

    // Check if restaurant has offline mode enabled and no online URL
    if (
      restaurantConfig?.hasOfflineMode &&
      restaurantConfig?.noOnlineUrl === true
    ) {
      if (
        !router.pathname.includes(PagesEnum.OfflineMode) &&
        !router.pathname.includes(PagesEnum.Login) &&
        !router.pathname.includes(PagesEnum.Verify) &&
        !router.pathname.includes(PagesEnum.LoyaltyTermsAndConditions)
      ) {
        router.push(getPageLink(router, PagesEnum.OfflineMode).as);
      }
    }
  }, [restaurantConfig, router.isReady, router.pathname, router]);

  return null;
}

function MyApp({ Component, pageProps }: MyAppProps) {
  const [loading] = useState(false);

  const router = useRouter();
  const { hasBackButton } = router.query;

  if (hasBackButton) {
    LS._set(LocalStorageEnum.HAS_BACK_BUTTON, true);
  }
  // Wrap the Component with authHOC
  const AuthWrappedComponent = authHOC(Component);

  useEffect(() => {
    const restaurantSocket = getSocket(
      SOCKET_NAMESPACE.RESTAURANT,
      env.restaurantId!,
    );
    restaurantSocket.on("connect", () => {
      console.log("restaurant socket connected");
    });

    let timer: any;
    restaurantSocket.on("connect_error", (err) => {
      console.log("restaurant socket connection error: ", err.message);
      timer = setTimeout(() => {
        restaurantSocket.io.opts.query = {
          ...restaurantSocket.io.opts.query,
          token: getCookie(COOKIE_ENUM.TOKEN),
        };
        restaurantSocket.disconnect().connect();
      }, 1000);
    });

    restaurantSocket.on("disconnect", () => {
      console.log("restaurant socket disconnected");
    });

    return () => {
      restaurantSocket.off("connect");
      restaurantSocket.off("connect_error");
      clearTimeout(timer);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  useEffect(() => {
    const headElement = document.querySelector("head");
    const config = { attributes: true, childList: true, subtree: true };

    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          const build_theme_style = document.querySelector("#theme_style");
          if (build_theme_style) {
            const resId = build_theme_style.getAttribute("data-id");
            const app_theme_style = document.querySelector(`#${resId}`);
            if (app_theme_style && app_theme_style.innerHTML.length > 0) {
              return;
            } else {
              const toBeDeleted =
                document.querySelectorAll("._app_theme_style");
              toBeDeleted.forEach((el) => {
                el.remove();
              });
              const sheet = document.createElement("style");
              sheet.classList.add("_app_theme_style");
              sheet.setAttribute("id", resId!);
              sheet.innerHTML = build_theme_style.innerHTML;
              document.body.appendChild(sheet);
            }
          }
        }
      }
    });

    observer.observe(headElement!, config);
  }, []);

  useEffect(() => {
    // This function will poll until both scripts are loaded
    const initAmplitude = () => {
      if (window.amplitude && window.sessionReplay) {
        const sessionReplayTracking = window.sessionReplay.plugin({
          sampleRate: 1,
        });

        window.amplitude.add(sessionReplayTracking);

        if (env.amplitudeApiKey && env.amplitudeApiKey.length > 0) {
          window.amplitude.init(env.amplitudeApiKey, {
            fetchRemoteConfig: true,
            serverZone: "EU" as const,
            autocapture: true,
          });
        }
      } else {
        setTimeout(initAmplitude, 100);
      }
    };

    initAmplitude();
  }, []);

  if (loading) {
    return <></>;
  }

  return LS._checkLocalStorageIfBlocked() ? (
    <CookieCheckPage />
  ) : !loading ? (
    <>
      {env.amplitudeApiKey && env.amplitudeApiKey.length > 0 && (
        <>
          <Script
            id="amplitude-session-replay-script"
            src="https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.19.3-min.js.gz"
            strategy="afterInteractive"
          />
          <Script
            id="amplitude-sdk-script"
            src={`https://cdn.amplitude.com/libs/analytics-browser-2.9.0-min.js.gz`}
            strategy="afterInteractive"
          />
        </>
      )}
      <ErrorBoundary>
        <Provider store={store}>
          <PersistGate loading={<LoadingDotBars />} persistor={persistor}>
            <AuthProvider>
              <BackgroundManager>
                <BackgroundHooks />
                <BackgroundRequests />
              </BackgroundManager>

              <OfflineModeRedirect />
              <NewLoading />
              <MainLayout>
                <AuthWrappedComponent {...pageProps} />
                <Modal />
                <OpenMobileApp />
              </MainLayout>
            </AuthProvider>
          </PersistGate>
        </Provider>
      </ErrorBoundary>
    </>
  ) : (
    <></>
  );
}

export default appWithTranslation(MyApp);
